/**
 * useStartContext — shared helper for the `/context/{context_id}/start` flow.
 *
 * Used by AssessmentRunner's "Next Lesson" button on the debrief screen.
 *
 * Returns the `lesson_id` of the first lesson the learner has not completed in
 * the given context. The caller is responsible for navigation.
 */
import { apiFetch } from '../api/client';

/**
 * Call `GET /context/{contextId}/start` and return the resulting `lesson_id`.
 *
 * Throws on non-OK responses:
 *  - 404 → "No lesson found for this context"
 *  - other → "Failed to start context: <statusText>"
 *
 * A 401 (session expired during a long check-in) is handled by the auth layer,
 * which sends the learner to sign in and back here afterwards.
 */
export async function startContext(contextId: string): Promise<string> {
  const response = await apiFetch(`/context/${contextId}/start`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('No lesson found for this context');
    }
    throw new Error(`Failed to start context: ${response.statusText}`);
  }

  const data = (await response.json()) as { lesson_id: string };
  return data.lesson_id;
}
